import { useState } from 'react';
import { Link } from 'react-router-dom';
import { test3PartI, test3PartII, test3PartIII, test3PartIV } from './data/regentsTest3';
import type { RegentsTestQuestion } from './data/regentsTest3';
import PartIMCQ from './components/PartIMCQ';
import OpenEndedQuestion from './components/OpenEndedQuestion';

type ActivePart = 'I' | 'II' | 'III' | 'IV';

const partInfo = [
  {
    part: 'I' as ActivePart,
    label: 'Part I',
    subtitle: 'Multiple Choice',
    questions: 24,
    points: 48,
    time: '~40 min',
    color: 'from-indigo-500 to-blue-600',
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
    text: 'text-indigo-700',
    icon: 'ri-checkbox-multiple-line',
  },
  {
    part: 'II' as ActivePart,
    label: 'Part II',
    subtitle: 'Short Answer',
    questions: 7,
    points: 14,
    time: '~25 min',
    color: 'from-indigo-600 to-purple-600',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-700',
    icon: 'ri-edit-2-line',
  },
  {
    part: 'III' as ActivePart,
    label: 'Part III',
    subtitle: 'Extended Response',
    questions: 3,
    points: 12,
    time: '~30 min',
    color: 'from-fuchsia-500 to-pink-500',
    bg: 'bg-fuchsia-50',
    border: 'border-fuchsia-200',
    text: 'text-fuchsia-700',
    icon: 'ri-file-text-line',
  },
  {
    part: 'IV' as ActivePart,
    label: 'Part IV',
    subtitle: 'Extended Response',
    questions: 1,
    points: 6,
    time: '~20 min',
    color: 'from-pink-500 to-fuchsia-600',
    bg: 'bg-pink-50',
    border: 'border-pink-200',
    text: 'text-pink-700',
    icon: 'ri-trophy-line',
  },
];

export default function GeometryRegentsTest3() {
  const [activePart, setActivePart] = useState<ActivePart | null>(null);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, number>>({});
  const [partISubmitted, setPartISubmitted] = useState(false);
  const [partIFilter, setPartIFilter] = useState<'all' | 'correct' | 'incorrect'>('all');

  const handleSelect = (qId: number, optIndex: number) => {
    if (partISubmitted) return;
    setSelectedAnswers(prev => ({ ...prev, [qId]: optIndex }));
  };

  const handlePartISubmit = () => {
    setPartISubmitted(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePartIReset = () => {
    setSelectedAnswers({});
    setPartISubmitted(false);
    setPartIFilter('all');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const partIScore = test3PartI.reduce((acc, q) => acc + (selectedAnswers[q.id] === q.correctAnswer ? 1 : 0), 0);
  const partIPoints = partIScore * 2;
  const partIPercent = Math.round((partIScore / test3PartI.length) * 100);
  const partIAnswered = Object.keys(selectedAnswers).length;

  const filteredPartI = test3PartI.filter(q => {
    if (partIFilter === 'all') return true;
    if (partIFilter === 'correct') return partISubmitted && selectedAnswers[q.id] === q.correctAnswer;
    if (partIFilter === 'incorrect') return partISubmitted && selectedAnswers[q.id] !== undefined && selectedAnswers[q.id] !== q.correctAnswer;
    return true;
  });

  const getScoreGradient = () => {
    if (partIPercent >= 85) return 'from-emerald-500 to-teal-600';
    if (partIPercent >= 65) return 'from-amber-500 to-orange-500';
    return 'from-red-500 to-rose-600';
  };

  const getScoreMsg = () => {
    if (partIPercent >= 85) return 'Excellent! You are Regents-ready!';
    if (partIPercent >= 65) return 'Good work — review the missed questions.';
    if (partIPercent >= 50) return 'Keep practicing — you are improving!';
    return 'Review the lessons and try again. You can do it!';
  };

  // ── Landing Page ──────────────────────────────────────────────────────────
  if (!activePart) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-blue-50 to-purple-50">
        {/* Navbar */}
        <header className="bg-white shadow-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-lg flex items-center justify-center shadow-md">
                <i className="ri-graduation-cap-fill text-white text-xl"></i>
              </div>
              <div>
                <p className="text-lg font-bold text-gray-900 leading-tight">Mr. Augustine's Math Academy</p>
                <p className="text-xs text-indigo-600 font-medium">NY Geometry Regents — Practice Test 3</p>
              </div>
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/grades/geometry-regents" className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm whitespace-nowrap">
                <i className="ri-arrow-left-line mr-1"></i>All Tests
              </Link>
              <Link to="/grades/grade-10" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium text-sm whitespace-nowrap">
                <i className="ri-book-open-line mr-1"></i>Lessons
              </Link>
            </div>
          </div>
        </header>

        {/* Hero */}
        <div className="bg-gradient-to-r from-indigo-900 via-blue-800 to-indigo-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="flex flex-col lg:flex-row items-center gap-12">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full text-sm font-semibold mb-5">
                  <span className="text-xl">📐</span>
                  Practice Test 3 — NY Geometry Regents
                </div>
                <h1 className="text-4xl md:text-5xl font-black mb-4 leading-tight">
                  Geometry Regents<br />
                  <span className="text-amber-300">Practice Test 3</span>
                </h1>
                <p className="text-indigo-100 text-lg leading-relaxed mb-6 max-w-xl">
                  Full-length practice exam with 35 brand-new questions covering all geometry topics.
                  Complete with step-by-step solutions, scoring rubrics, and detailed explanations.
                </p>
                <div className="flex flex-wrap gap-3 mb-8">
                  {[
                    { icon: 'ri-question-line', label: '35 Questions' },
                    { icon: 'ri-award-line', label: '86 Points' },
                    { icon: 'ri-time-line', label: '~3 Hours' },
                    { icon: 'ri-file-text-line', label: 'All 4 Parts' },
                  ].map((b, i) => (
                    <div key={i} className="flex items-center gap-2 bg-white/15 px-4 py-2 rounded-full text-sm font-medium">
                      <i className={`${b.icon} text-amber-300`}></i>
                      {b.label}
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => setActivePart('I')}
                  className="px-8 py-4 bg-amber-400 text-gray-900 rounded-xl font-black text-lg hover:bg-amber-300 transition-colors shadow-lg whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-play-circle-fill mr-2"></i>Start Test 3
                </button>
              </div>

              {/* Score Card */}
              <div className="flex-shrink-0 bg-white/10 backdrop-blur rounded-2xl border border-white/20 p-6 w-full max-w-sm">
                <h3 className="text-lg font-bold mb-4 text-center">Test Structure</h3>
                <div className="space-y-3">
                  {partInfo.map(p => (
                    <div key={p.part} className="flex items-center justify-between bg-white/10 rounded-xl px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-br ${p.color}`}>
                          <i className={`${p.icon} text-white text-sm`}></i>
                        </div>
                        <div>
                          <p className="font-bold text-sm">{p.label}</p>
                          <p className="text-white/70 text-xs">{p.subtitle}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-sm">{p.points} pts</p>
                        <p className="text-white/70 text-xs">{p.questions} Qs</p>
                      </div>
                    </div>
                  ))}
                  <div className="border-t border-white/20 pt-3 flex justify-between items-center">
                    <span className="font-bold text-white">Total</span>
                    <span className="font-black text-amber-300 text-lg">86 Points</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Part Cards */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Choose a Part to Practice</h2>
          <p className="text-gray-500 text-sm mb-8">Practice each part independently with complete solutions and rubrics.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {partInfo.map(p => (
              <div key={p.part} className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden group">
                <div className={`bg-gradient-to-r ${p.color} p-6 text-white`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                        <i className={`${p.icon} text-2xl`}></i>
                      </div>
                      <div>
                        <p className="text-white/80 text-xs font-medium uppercase tracking-wide">{p.subtitle}</p>
                        <p className="text-2xl font-black">{p.label}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-black">{p.points}</p>
                      <p className="text-white/80 text-xs">points</p>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-5 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <i className="ri-question-line text-indigo-500"></i>
                      <span>{p.questions} question{p.questions > 1 ? 's' : ''}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <i className="ri-time-line text-indigo-500"></i>
                      <span>{p.time}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => setActivePart(p.part)}
                    className={`w-full py-3 bg-gradient-to-r ${p.color} text-white rounded-xl font-bold text-sm hover:opacity-90 transition-opacity shadow-md whitespace-nowrap cursor-pointer`}
                  >
                    <i className="ri-play-circle-fill mr-2"></i>
                    Start {p.label}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── Part I View ───────────────────────────────────────────────────────────
  if (activePart === 'I') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-blue-50 to-purple-50">
        <div className="bg-gradient-to-r from-indigo-600 to-blue-700 text-white sticky top-0 z-40 shadow-lg">
          <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg">
                <i className="ri-checkbox-multiple-fill text-xl"></i>
              </div>
              <div>
                <h1 className="text-lg font-bold">Part I — Multiple Choice</h1>
                <p className="text-white/80 text-xs">24 questions · 48 points · Test 3</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {!partISubmitted && (
                <span className="text-sm text-white/80 whitespace-nowrap">{partIAnswered}/24 answered</span>
              )}
              <button onClick={() => setActivePart(null)} className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-arrow-left-line mr-1"></i>Back
              </button>
            </div>
          </div>
          {!partISubmitted && (
            <div className="max-w-5xl mx-auto px-4 pb-3">
              <div className="w-full bg-white/20 rounded-full h-1.5">
                <div className="bg-white rounded-full h-1.5 transition-all duration-300" style={{ width: `${(partIAnswered / 24) * 100}%` }} />
              </div>
            </div>
          )}
        </div>

        <div className="max-w-5xl mx-auto px-4 py-8">
          {partISubmitted && (
            <div className={`bg-gradient-to-r ${getScoreGradient()} rounded-2xl p-8 text-white mb-8 shadow-xl`}>
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="text-center">
                  <div className="text-7xl font-black">{partIPercent}%</div>
                  <div className="text-white/80 text-sm mt-1">Part I Score</div>
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold mb-2">{getScoreMsg()}</h2>
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    <div className="bg-white/20 rounded-xl p-3 text-center">
                      <div className="text-2xl font-bold">{partIScore}</div>
                      <div className="text-xs text-white/80">Correct</div>
                    </div>
                    <div className="bg-white/20 rounded-xl p-3 text-center">
                      <div className="text-2xl font-bold">{24 - partIScore}</div>
                      <div className="text-xs text-white/80">Incorrect</div>
                    </div>
                    <div className="bg-white/20 rounded-xl p-3 text-center">
                      <div className="text-2xl font-bold">{partIPoints}/48</div>
                      <div className="text-xs text-white/80">Points</div>
                    </div>
                  </div>
                  <div className="mt-4 flex gap-3 flex-wrap">
                    <button onClick={handlePartIReset} className="px-5 py-2 bg-white text-gray-800 rounded-lg font-bold text-sm hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
                      <i className="ri-refresh-line mr-1"></i>Retake Part I
                    </button>
                    <button onClick={() => setActivePart('II')} className="px-5 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-bold text-sm transition-colors whitespace-nowrap cursor-pointer">
                      <i className="ri-arrow-right-line mr-1"></i>Continue to Part II
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {partISubmitted && (
            <div className="flex gap-2 mb-6 flex-wrap">
              {(['all', 'correct', 'incorrect'] as const).map(f => (
                <button
                  key={f}
                  onClick={() => setPartIFilter(f)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    partIFilter === f
                      ? f === 'correct' ? 'bg-emerald-600 text-white' : f === 'incorrect' ? 'bg-red-500 text-white' : 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {f === 'all' && `All (24)`}
                  {f === 'correct' && `Correct (${partIScore})`}
                  {f === 'incorrect' && `Incorrect (${24 - partIScore})`}
                </button>
              ))}
            </div>
          )}

          <PartIMCQ
            questions={filteredPartI}
            submitted={partISubmitted}
            selectedAnswers={selectedAnswers}
            onSelect={handleSelect}
          />

          {!partISubmitted && (
            <div className="mt-10 flex flex-col items-center gap-4">
              <p className="text-sm text-gray-500">
                {partIAnswered < 24 ? `${24 - partIAnswered} question(s) unanswered` : 'All questions answered — ready to submit!'}
              </p>
              <button
                onClick={handlePartISubmit}
                disabled={partIAnswered === 0}
                className={`px-10 py-4 rounded-xl font-bold text-lg shadow-lg transition-all whitespace-nowrap cursor-pointer ${
                  partIAnswered > 0
                    ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white hover:from-indigo-700 hover:to-blue-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                <i className="ri-send-plane-fill mr-2"></i>Submit Part I ({partIAnswered}/24)
              </button>
            </div>
          )}

          {partISubmitted && (
            <div className="mt-10 flex justify-center gap-4 flex-wrap">
              <button onClick={handlePartIReset} className="px-8 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-refresh-line mr-2"></i>Retake Part I
              </button>
              <button onClick={() => setActivePart('II')} className="px-8 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-arrow-right-line mr-2"></i>Continue to Part II
              </button>
              <button onClick={() => setActivePart(null)} className="px-8 py-3 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-arrow-left-line mr-2"></i>Back to Overview
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Parts II, III, IV View ────────────────────────────────────────────────
  const openPartData: RegentsTestQuestion[] = activePart === 'II' ? test3PartII : activePart === 'III' ? test3PartIII : test3PartIV;
  const currentPartInfo = partInfo.find(p => p.part === activePart)!;
  const nextPart: Record<string, ActivePart | null> = { II: 'III', III: 'IV', IV: null };
  const prevPart: Record<string, ActivePart> = { II: 'I', III: 'II', IV: 'III' };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-blue-50 to-purple-50">
      <div className={`bg-gradient-to-r ${currentPartInfo.color} text-white sticky top-0 z-40 shadow-lg`}>
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg">
              <i className={`${currentPartInfo.icon} text-xl`}></i>
            </div>
            <div>
              <h1 className="text-lg font-bold">{currentPartInfo.label} — {currentPartInfo.subtitle}</h1>
              <p className="text-white/80 text-xs">
                {currentPartInfo.questions} question{currentPartInfo.questions > 1 ? 's' : ''} · {currentPartInfo.points} points · Test 3
              </p>
            </div>
          </div>
          <button onClick={() => setActivePart(null)} className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-arrow-left-line mr-1"></i>Back
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="space-y-8">
          {openPartData.map((q, idx) => (
            <OpenEndedQuestion
              key={q.id}
              question={q}
              qNumber={idx + 1}
              submitted={false}
            />
          ))}
        </div>

        <div className="mt-12 flex flex-wrap justify-between items-center gap-4">
          <button
            onClick={() => setActivePart(prevPart[activePart])}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line mr-2"></i>Back to {currentPartInfo.label === 'Part II' ? 'Part I' : currentPartInfo.label === 'Part III' ? 'Part II' : 'Part III'}
          </button>
          <div className="flex gap-3 flex-wrap">
            <button onClick={() => setActivePart(null)} className="px-6 py-3 bg-white border border-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-home-line mr-2"></i>Test Overview
            </button>
            {nextPart[activePart] && (
              <button
                onClick={() => setActivePart(nextPart[activePart] as ActivePart)}
                className={`px-6 py-3 bg-gradient-to-r ${currentPartInfo.color} text-white rounded-xl font-bold hover:opacity-90 transition-opacity whitespace-nowrap cursor-pointer`}
              >
                Continue to {activePart === 'II' ? 'Part III' : 'Part IV'}<i className="ri-arrow-right-line ml-2"></i>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}